// ═══════════════════════════════════════════════════════════
// Request Detail — full request view with all tabs
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  IconArrowLeft, IconClock, IconUser, IconBuilding,
  IconFileText, IconMail, IconListCheck, IconHistory,
  IconPaperclip, IconEdit, IconAlertTriangle, IconLoader2,
  IconChevronDown, IconCheck,
} from '@tabler/icons-react';
import { CLOCK_COLORS, formatDaysRemaining } from '../../../lib/deadlineClock';
import { useRequestStore } from '../../../store/useRequestStore';
import { useAuthStore } from '../../../store/authStore';
import {
  getRequest, getRequester, updateRequest, pauseClock, resumeClock, grantExtension,
  type FoiaRequest, type Requester, type RequestStatus,
  type RequestChannel, type RequestPriority,
} from '../../../api/requests';
import { listJurisdictions } from '../../../api/jurisdiction';
import { listDepartments, type Department } from '../../../api/departments';
import { classifyRequest, type AIClassificationResult } from '../../../api/ai';
import DenialModal from './DenialModal';
import { DropdownMenu } from '../../../components/ui/DropdownMenu';
import { formatDate, formatPhone } from '../../../lib/format';
import { listCorrespondenceForRequest, type CorrespondenceRecord } from '../../../api/correspondence';
import TasksTab from './tabs/TasksTab';
import TimelineTab from './tabs/TimelineTab';
import CorrespondenceTab from './tabs/CorrespondenceTab';
import DocumentsTab from './tabs/DocumentsTab';
import InvoicesTab from './tabs/InvoicesTab';

// ── Constants ─────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', acknowledged: 'Acknowledged',
  inProgress: 'In Progress', awaitingRequester: 'Awaiting Requester',
  awaitingPayment: 'Awaiting Payment', pendingReview: 'Pending Review',
  completed: 'Completed', denied: 'Denied', withdrawn: 'Withdrawn', onHold: 'On Hold',
};

const STATUS_STYLE: Record<string, { background: string; color: string }> = {
  submitted:         { background: 'var(--cds-cerulean-50)',  color: 'var(--cds-cerulean-700)' },
  acknowledged:      { background: 'var(--cds-green-50)',     color: 'var(--cds-green-700)'    },
  inProgress:        { background: 'var(--cds-blurple-50)',   color: 'var(--cds-blurple-700)'  },
  awaitingRequester: { background: 'var(--cds-yellow-50)',    color: 'var(--cds-yellow-700)'   },
  awaitingPayment:   { background: 'var(--cds-yellow-50)',    color: 'var(--cds-yellow-700)'   },
  pendingReview:     { background: 'var(--cds-violet-50)',    color: 'var(--cds-violet-700)'   },
  completed:         { background: 'var(--cds-green-50)',     color: 'var(--cds-green-700)'    },
  denied:            { background: 'var(--cds-red-50)',       color: 'var(--cds-red-700)'      },
  withdrawn:         { background: 'var(--cds-gray-50)',      color: 'var(--cds-gray-700)'     },
  onHold:            { background: 'var(--cds-gray-50)',      color: 'var(--cds-gray-700)'     },
  draft:             { background: 'var(--cds-gray-50)',      color: 'var(--cds-gray-700)'     },
};

const TRANSITIONS: Record<string, RequestStatus[]> = {
  submitted:         ['acknowledged', 'inProgress', 'completed', 'onHold', 'denied', 'withdrawn'],
  acknowledged:      ['inProgress', 'awaitingRequester', 'awaitingPayment', 'completed', 'onHold', 'denied', 'withdrawn'],
  inProgress:        ['pendingReview', 'awaitingRequester', 'awaitingPayment', 'completed', 'denied', 'onHold'],
  awaitingRequester: ['inProgress', 'completed', 'withdrawn'],
  awaitingPayment:   ['inProgress', 'completed', 'withdrawn'],
  pendingReview:     ['inProgress', 'completed', 'denied'],
  onHold:            ['inProgress', 'completed', 'acknowledged'],
  completed:         [],
  denied:            [],
  withdrawn:         [],
  draft:             ['submitted'],
};

const TABS = [
  { id: 'overview',       label: 'Overview',       icon: IconFileText },
  { id: 'documents',      label: 'Documents',      icon: IconPaperclip },
  { id: 'tasks',          label: 'Tasks',          icon: IconListCheck },
  { id: 'correspondence', label: 'Correspondence', icon: IconMail },
  { id: 'invoices',       label: 'Invoice',        icon: IconEdit },
  { id: 'timeline',       label: 'Timeline',       icon: IconHistory },
];

// ── Phase stepper ─────────────────────────────────────────

const PHASES = [
  { number: 1, label: 'Received',        targetStatus: 'acknowledged'      as RequestStatus, statuses: ['submitted', 'acknowledged'] },
  { number: 2, label: 'Under Review',    targetStatus: 'inProgress'        as RequestStatus, statuses: ['inProgress'] },
  { number: 3, label: 'Collecting',      targetStatus: 'awaitingRequester' as RequestStatus, statuses: ['awaitingRequester', 'awaitingPayment', 'onHold'] },
  { number: 4, label: 'Pending Release', targetStatus: 'pendingReview'     as RequestStatus, statuses: ['pendingReview'] },
  { number: 5, label: 'Closed',          targetStatus: 'completed'         as RequestStatus, statuses: ['completed', 'denied', 'withdrawn'] },
];

function getPhase(status: string): number {
  return PHASES.find(p => p.statuses.includes(status))?.number ?? 1;
}

function PhaseBar({ request, actorEmail, onUpdated }: {
  request: FoiaRequest; actorEmail?: string; onUpdated: (r: FoiaRequest) => void;
}) {
  const [advancing, setAdvancing] = useState<number | null>(null);
  const currentPhase = getPhase(request.status);
  const isClosed = currentPhase === 5;

  async function advanceTo(phaseNum: number, targetStatus: RequestStatus) {
    setAdvancing(phaseNum);
    try {
      await updateRequest(request.recordId, { status: targetStatus }, actorEmail);
      const refreshed = await getRequest(request.recordId);
      if (refreshed) onUpdated(refreshed);
    } finally {
      setAdvancing(null);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', padding: '10px 0', marginBottom: 16, borderBottom: '1px solid var(--color-neutral-200)' }}>
      {PHASES.map((phase, i) => {
        const isComplete  = currentPhase > phase.number;
        const isCurrent   = currentPhase === phase.number;
        const isClickable = !isClosed && phase.number > currentPhase && phase.number < 5;
        return (
          <div key={phase.number} style={{ display: 'flex', alignItems: 'flex-start', flex: i < PHASES.length - 1 ? 1 : 0 }}>
            <button
              title={isClickable ? `Advance to: ${phase.label}` : phase.label}
              disabled={!isClickable || advancing !== null}
              onClick={() => isClickable && advanceTo(phase.number, phase.targetStatus)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                border: 'none', background: 'none', padding: '0 2px', flexShrink: 0,
                cursor: isClickable ? 'pointer' : 'default',
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                background: (isComplete || isCurrent) ? 'var(--color-primary-700)' : 'var(--color-neutral-100)',
                color: (isComplete || isCurrent) ? 'white' : 'var(--color-neutral-400)',
                border: `2px solid ${(isComplete || isCurrent) ? 'var(--color-primary-700)' : 'var(--color-neutral-200)'}`,
                outline: isCurrent ? '3px solid var(--color-primary-100, #E0E7FF)' : 'none',
                transition: 'all 0.15s',
              }}>
                {advancing === phase.number
                  ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                  : isComplete ? <IconCheck size={11} /> : phase.number
                }
              </div>
              <span style={{
                fontSize: 11, fontWeight: isCurrent ? 700 : 400, whiteSpace: 'nowrap',
                color: isCurrent ? 'var(--color-primary-700)' : isComplete ? 'var(--color-neutral-600)' : 'var(--color-neutral-400)',
              }}>
                {phase.label}
              </span>
            </button>
            {i < PHASES.length - 1 && (
              <div style={{
                flex: 1, height: 2, marginTop: 13,
                background: isComplete ? 'var(--color-primary-300, #A5B4FC)' : 'var(--color-neutral-200)',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Pause Clock Modal ─────────────────────────────────────

function PauseClockModal({
  requestId, actorEmail, onPaused, onClose,
}: {
  requestId: number; actorEmail?: string;
  onPaused: () => void; onClose: () => void;
}) {
  const [reason, setReason] = useState('awaitingRequester');
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await pauseClock(requestId, reason, notes, actorEmail);
      onPaused();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}>
      <div style={{
        background: 'white', borderRadius: 4, padding: 24,
        width: 420, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 16px', color: 'var(--color-neutral-900)' }}>
          Pause Statutory Clock
        </h2>
        <div className="form-field">
          <label className="form-label">Reason</label>
          <select className="form-input" value={reason} onChange={e => setReason(e.target.value)}>
            <option value="awaitingRequester">Awaiting Requester Response</option>
            <option value="awaitingPayment">Awaiting Payment</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="form-field" style={{ marginBottom: 24 }}>
          <label className="form-label">Notes (optional)</label>
          <textarea
            className="form-input"
            rows={3}
            style={{ resize: 'none', fontFamily: 'inherit' }}
            placeholder="Clarification requested re: date range…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
            {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Pause Clock'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status Dropdown ───────────────────────────────────────

function StatusDropdown({
  request, actorEmail, onUpdated,
}: {
  request: FoiaRequest; actorEmail?: string; onUpdated: (r: FoiaRequest) => void;
}) {
  const [open, setOpen]     = useState(false);
  const [saving, setSaving] = useState(false);
  const [closureReason, setClosureReason] = useState('');
  const [pendingStatus, setPendingStatus] = useState<RequestStatus | null>(null);

  const transitions = TRANSITIONS[request.status] ?? [];
  const isClosure = ['completed', 'denied', 'withdrawn'].includes(pendingStatus ?? '');

  async function apply(status: RequestStatus, reason?: string) {
    setSaving(true);
    try {
      await updateRequest(request.recordId, { status, closureReason: reason }, actorEmail);
      onUpdated({ ...request, status });
    } finally {
      setSaving(false);
      setOpen(false);
      setPendingStatus(null);
      setClosureReason('');
    }
  }

  const s = STATUS_STYLE[request.status] ?? {};

  return (
    <div style={{ position: 'relative' }}>
      {pendingStatus && isClosure && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{ background: 'white', borderRadius: 4, padding: 24, width: 400, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>
              {STATUS_LABELS[pendingStatus]} — Closure Reason
            </h2>
            <div className="form-field" style={{ marginBottom: 16 }}>
              <label className="form-label">Reason (optional)</label>
              <textarea
                className="form-input" rows={3} style={{ fontFamily: 'inherit' }}
                value={closureReason}
                onChange={e => setClosureReason(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setPendingStatus(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => apply(pendingStatus, closureReason)}>
                {saving ? 'Saving…' : `Mark ${STATUS_LABELS[pendingStatus]}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <DropdownMenu
        open={open}
        onClose={() => setOpen(false)}
        align="right"
        minWidth={180}
        trigger={
          <button
            className="btn btn-secondary btn-sm"
            style={{ gap: 4, ...s, border: `1px solid ${s.color}30` }}
            onClick={() => setOpen(o => !o)}
            disabled={transitions.length === 0 || saving}
          >
            {saving
              ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              : STATUS_LABELS[request.status]
            }
            {transitions.length > 0 && <IconChevronDown size={11} />}
          </button>
        }
      >
        <div style={{ padding: '6px 12px 4px', fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Change Status
        </div>
        {transitions.map(status => {
          const ss = STATUS_STYLE[status] ?? {};
          const isClos = ['completed', 'denied', 'withdrawn'].includes(status);
          return (
            <button
              key={status}
              onClick={() => {
                setOpen(false);
                if (isClos) { setPendingStatus(status); }
                else apply(status);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 12px', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 14, color: 'var(--color-neutral-700)',
                textAlign: 'left', borderTop: '1px solid var(--color-neutral-100)',
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: ss.color ?? 'var(--color-neutral-500)', flexShrink: 0,
              }} />
              {STATUS_LABELS[status]}
              {request.status === status && <IconCheck size={11} style={{ marginLeft: 'auto', color: ss.color }} />}
            </button>
          );
        })}
      </DropdownMenu>
    </div>
  );
}

// ── Assign Dept Dropdown ──────────────────────────────────

function AssignDeptDropdown({
  request, departments, actorEmail, onUpdated,
}: {
  request: FoiaRequest; departments: Department[]; actorEmail?: string;
  onUpdated: (r: FoiaRequest) => void;
}) {
  const [open, setOpen]     = useState(false);
  const [saving, setSaving] = useState(false);

  const currentDept = departments.find(d => String(d.recordId) === request.assignedDeptId);

  async function assign(deptId: string) {
    setSaving(true);
    setOpen(false);
    try {
      await updateRequest(request.recordId, { assignedDeptId: deptId }, actorEmail);
      onUpdated({ ...request, assignedDeptId: deptId });
    } finally {
      setSaving(false);
    }
  }

  return (
    <DropdownMenu
      open={open}
      onClose={() => setOpen(false)}
      align="left"
      minWidth={160}
      trigger={
        <button
          className="btn btn-secondary btn-sm"
          style={{ gap: 4 }}
          onClick={() => setOpen(o => !o)}
          disabled={saving}
        >
          <span style={{ color: currentDept ? 'var(--color-neutral-800)' : 'var(--color-neutral-400)' }}>
            {saving ? 'Saving…' : (currentDept?.name ?? 'Unassigned')}
          </span>
          <IconChevronDown size={11} />
        </button>
      }
    >
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        <button
          onClick={() => assign('')}
          style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 12, color: 'var(--color-neutral-500)' }}
        >
          — Unassign —
        </button>
        {departments.map(d => (
          <button
            key={d.recordId}
            onClick={() => assign(String(d.recordId))}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', padding: '8px 12px', border: 'none', background: 'none',
              cursor: 'pointer', textAlign: 'left', fontSize: 12,
              color: 'var(--color-neutral-800)',
              fontWeight: String(d.recordId) === request.assignedDeptId ? 600 : 400,
              borderTop: '1px solid var(--color-neutral-100)',
            }}
          >
            {d.name}
            {String(d.recordId) === request.assignedDeptId && <IconCheck size={11} style={{ color: 'var(--color-primary-600)' }} />}
          </button>
        ))}
      </div>
    </DropdownMenu>
  );
}

// ── Assignee Dropdown ─────────────────────────────────────

function AssigneeDropdown({
  request, actorEmail, onUpdated,
}: {
  request: FoiaRequest; actorEmail?: string; onUpdated: (r: FoiaRequest) => void;
}) {
  const [open, setOpen]     = useState(false);
  const [saving, setSaving] = useState(false);

  const assignee = request.assignedUserId || null;
  const isMe = assignee === actorEmail;
  const shortName = assignee ? assignee.split('@')[0] : null;

  async function assign(userId: string) {
    setSaving(true);
    setOpen(false);
    try {
      await updateRequest(request.recordId, { assignedUserId: userId }, actorEmail);
      onUpdated({ ...request, assignedUserId: userId });
    } finally {
      setSaving(false);
    }
  }

  return (
    <DropdownMenu
      open={open}
      onClose={() => setOpen(false)}
      align="left"
      minWidth={160}
      trigger={
        <button
          className="btn btn-secondary btn-sm"
          style={{ gap: 4 }}
          onClick={() => setOpen(o => !o)}
          disabled={saving}
        >
          <IconUser size={11} />
          <span style={{ color: assignee ? 'var(--color-neutral-800)' : 'var(--color-neutral-400)' }}>
            {saving ? 'Saving…' : (shortName ?? 'Unassigned')}
          </span>
          <IconChevronDown size={11} />
        </button>
      }
    >
      {actorEmail && !isMe && (
        <button
          onClick={() => assign(actorEmail)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, width: '100%',
            padding: '8px 12px', border: 'none', background: 'none',
            cursor: 'pointer', textAlign: 'left', fontSize: 12,
            color: 'var(--color-neutral-800)',
          }}
        >
          <IconUser size={11} /> Assign to me
        </button>
      )}
      {assignee && (
        <button
          onClick={() => assign('')}
          style={{
            display: 'block', width: '100%', padding: '8px 12px', border: 'none',
            background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 12,
            color: 'var(--color-neutral-500)',
            borderTop: actorEmail && !isMe ? '1px solid var(--color-neutral-100)' : 'none',
          }}
        >
          — Unassign —
        </button>
      )}
      {!actorEmail && !assignee && (
        <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--color-neutral-400)' }}>
          No options available
        </div>
      )}
    </DropdownMenu>
  );
}

// ── Edit Request Modal ────────────────────────────────────

function EditRequestModal({ request, actorEmail, onSaved, onClose }: {
  request: FoiaRequest;
  actorEmail: string;
  onSaved: (updated: FoiaRequest) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    subject: request.subject,
    description: request.description,
    requestType: request.requestType ?? 'FOIA',
    priority: request.priority ?? 'normal',
    channel: request.requestChannel ?? 'portal',
    internalNotes: request.internalNotes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateRequest(request.recordId, {
        subject: form.subject,
        description: form.description,
        requestType: form.requestType,
        channel: form.channel as RequestChannel,
        priority: form.priority as RequestPriority,
        internalNotes: form.internalNotes,
      }, actorEmail);
      // updateRequest returns Record<string, unknown> — re-fetch the full request
      const updated = await getRequest(request.recordId);
      if (updated) onSaved(updated);
      else onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 4, padding: '24px', width: 560, maxWidth: '90vw', maxHeight: '85vh', overflow: 'auto', boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)' }}>Edit Request</div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div style={{ padding: '8px 12px', background: 'var(--cds-red-50)', border: '1px solid #F7D5D0', borderRadius: 4, fontSize: 14, color: 'var(--cds-red-700)' }}>{error}</div>}

          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label required">Subject</label>
            <input type="text" className="form-input" required value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
          </div>

          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Request Type</label>
              <input type="text" className="form-input" value={form.requestType} onChange={e => setForm(f => ({ ...f, requestType: e.target.value }))} />
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Priority</label>
              <select className="form-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as typeof f.priority }))}>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Channel</label>
              <select className="form-select" value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value as typeof f.channel }))}>
                <option value="portal">Portal</option>
                <option value="email">Email</option>
                <option value="mail">Mail</option>
                <option value="inPerson">In Person</option>
                <option value="phone">Phone</option>
              </select>
            </div>
          </div>

          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Internal Notes</label>
            <textarea className="form-textarea" rows={3} value={form.internalNotes} onChange={e => setForm(f => ({ ...f, internalNotes: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ gap: 8 }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Awaiting Requester Banner ─────────────────────────────

function AwaitingRequesterBanner({
  request, actorEmail, onResolved, onGoToCorrespondence,
}: {
  request: FoiaRequest;
  actorEmail?: string;
  onResolved: (r: FoiaRequest) => void;
  onGoToCorrespondence: () => void;
}) {
  const [resolving, setResolving] = useState(false);

  async function handleResolve() {
    setResolving(true);
    try {
      await updateRequest(request.recordId, { status: 'inProgress' }, actorEmail);
      const refreshed = await getRequest(request.recordId);
      if (refreshed) onResolved(refreshed);
    } finally {
      setResolving(false);
    }
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      background: 'var(--cds-yellow-50)', border: '1px solid #FCD8A3', borderRadius: 4,
      padding: '10px 16px', marginBottom: 16,
    }}>
      <IconAlertTriangle size={16} style={{ color: 'var(--cds-yellow-600)', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--cds-yellow-800)' }}>
          Awaiting Requester Response
        </div>
        <div style={{ fontSize: 12, color: 'var(--cds-yellow-700)', marginTop: 2 }}>
          An action has been sent to the requester. The request is on hold until they respond.
        </div>
      </div>
      <button
        className="btn btn-secondary btn-sm"
        style={{ fontSize: 12, whiteSpace: 'nowrap' }}
        onClick={onGoToCorrespondence}
      >
        View Message
      </button>
      <button
        className="btn btn-secondary btn-sm"
        style={{ fontSize: 12, whiteSpace: 'nowrap', gap: 4 }}
        disabled={resolving}
        onClick={handleResolve}
      >
        {resolving
          ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
          : <><IconCheck size={11} /> Mark Resolved</>
        }
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') ?? 'overview');
  const [loading, setLoading]     = useState(false);
  const [request, setRequest]     = useState<FoiaRequest | null>(null);
  const [requester, setRequester] = useState<Requester | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showDenialModal, setShowDenialModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extensionDays, setExtensionDays] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  const [extensionSaving, setExtensionSaving] = useState(false);
  const [maxExtensionDays, setMaxExtensionDays] = useState<number>(30);
  const [aiResult, setAiResult]   = useState<AIClassificationResult | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [internalNotes, setInternalNotes] = useState<CorrespondenceRecord[]>([]);

  const storeRequests = useRequestStore(s => s.requests);
  const upsert = useRequestStore(s => s.upsert);
  const authUser = useAuthStore(s => s.user);
  const actorEmail = authUser?.email;

  useEffect(() => {
    if (!id) return;
    const recordId = parseInt(id, 10);
    if (isNaN(recordId)) return;

    // Check store cache first
    const cached = storeRequests.find(r => r.recordId === recordId);
    if (cached) {
      setRequest(cached);
    } else {
      setLoading(true);
      getRequest(recordId).then(r => setRequest(r)).finally(() => setLoading(false));
    }

    listDepartments().then(setDepartments);
  }, [id]);

  // Load requester when request is available
  useEffect(() => {
    if (!request?.requesterId) return;
    getRequester(request.requesterId).then(setRequester);
  }, [request?.requesterId]);

  // Load internal notes for the overview panel; re-fetch when switching back to overview
  useEffect(() => {
    if (!request?.recordId) return;
    if (activeTab !== 'overview') return;
    listCorrespondenceForRequest(request.recordId)
      .then(all => setInternalNotes(all.filter(c => c.direction === 'internal')));
  }, [request?.recordId, activeTab]);

  function handleRequestUpdate(updated: FoiaRequest) {
    setRequest(updated);
    upsert(updated);
  }

  async function handleGrantExtension() {
    if (!request) return;
    const days = parseInt(extensionDays, 10);
    if (isNaN(days) || days < 1) return;
    setExtensionSaving(true);
    try {
      const statutory = request.statutoryDeadline ?? request.effectiveDeadline ?? new Date().toISOString().split('T')[0];
      await grantExtension(request.recordId, days, extensionReason, statutory, actorEmail);
      const refreshed = await getRequest(request.recordId);
      if (refreshed) handleRequestUpdate(refreshed);
      setShowExtensionModal(false);
      setExtensionDays('');
      setExtensionReason('');
    } finally {
      setExtensionSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '48px 32px', textAlign: 'center', color: 'var(--color-neutral-400)' }}>
        <IconLoader2 size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
        <div style={{ fontSize: 14 }}>Loading request…</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div style={{ padding: '48px 32px', textAlign: 'center', color: 'var(--color-neutral-400)' }}>
        <p>Request not found.</p>
        <Link to="/staff/requests" style={{ color: 'var(--color-primary-700)', fontSize: 14 }}>← Back to requests</Link>
      </div>
    );
  }

  const clkStatus = (request.clockStatus ?? 'on-track') as keyof typeof CLOCK_COLORS;
  const clk = CLOCK_COLORS[clkStatus] ?? CLOCK_COLORS['on-track'];
  const isClosed = ['completed', 'denied', 'withdrawn'].includes(request.status);
  const currentDept = departments.find(d => String(d.recordId) === request.assignedDeptId);

  return (
    <div style={{ padding: 0 }}>
      {showDenialModal && (
        <DenialModal
          request={request}
          requester={requester}
          actorEmail={actorEmail}
          onDenied={() => {
            setShowDenialModal(false);
            getRequest(request.recordId).then(r => r && handleRequestUpdate(r));
          }}
          onClose={() => setShowDenialModal(false)}
        />
      )}

      {showPauseModal && (
        <PauseClockModal
          requestId={request.recordId}
          actorEmail={actorEmail}
          onPaused={() => {
            setShowPauseModal(false);
            getRequest(request.recordId).then(r => r && handleRequestUpdate(r));
          }}
          onClose={() => setShowPauseModal(false)}
        />
      )}

      {showEditModal && (
        <EditRequestModal
          request={request}
          actorEmail={actorEmail ?? ''}
          onSaved={(updated) => { handleRequestUpdate(updated); setShowEditModal(false); }}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showExtensionModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{ background: 'white', borderRadius: 4, padding: 24, width: 420, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px', color: 'var(--color-neutral-900)' }}>
              Grant Extension
            </h2>
            <p style={{ fontSize: 13, color: 'var(--color-neutral-500)', margin: '0 0 20px' }}>
              Extends the statutory deadline. Maximum allowed: {maxExtensionDays} days.
            </p>
            <div className="form-field">
              <label className="form-label">Extension Days</label>
              <input
                type="number"
                className="form-input"
                min={1}
                max={maxExtensionDays}
                value={extensionDays}
                onChange={e => setExtensionDays(e.target.value)}
                placeholder={`1–${maxExtensionDays}`}
              />
            </div>
            <div className="form-field" style={{ marginBottom: 24 }}>
              <label className="form-label">Reason</label>
              <textarea
                className="form-input"
                rows={3}
                style={{ resize: 'none', fontFamily: 'inherit' }}
                placeholder="Unusual volume of records, third-party consultation required…"
                value={extensionReason}
                onChange={e => setExtensionReason(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowExtensionModal(false); setExtensionDays(''); setExtensionReason(''); }}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                disabled={extensionSaving || !extensionDays || parseInt(extensionDays, 10) < 1 || parseInt(extensionDays, 10) > maxExtensionDays}
                onClick={handleGrantExtension}
              >
                {extensionSaving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Grant Extension'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--color-neutral-500)' }}>
        <Link to="/staff/requests" style={{ color: 'var(--color-primary-700)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
          <IconArrowLeft size={13} /> Requests
        </Link>
        <span>/</span>
        <span style={{ color: 'var(--color-neutral-800)', fontWeight: 500 }}>{request.requestNumber || `#${id}`}</span>
      </div>

      {/* Title + action buttons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: 'var(--color-primary-700)' }}>
              {request.requestNumber}
            </span>
            {request.priority !== 'normal' && (
              <span style={{ fontSize: 12, fontWeight: 700, color: request.priority === 'urgent' ? 'var(--cds-red-700)' : 'var(--cds-yellow-600)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {request.priority}
              </span>
            )}
          </div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0, lineHeight: 1.3 }}>
            {request.subject}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Department inline in button row */}
          {departments.length > 0 ? (
            <AssignDeptDropdown request={request} departments={departments} actorEmail={actorEmail} onUpdated={handleRequestUpdate} />
          ) : (
            <span style={{ fontSize: 13, color: 'var(--color-neutral-500)' }}>{currentDept?.name ?? 'Unassigned'}</span>
          )}
          <AssigneeDropdown request={request} actorEmail={actorEmail} onUpdated={handleRequestUpdate} />
          <span style={{ width: 1, height: 20, background: 'var(--color-neutral-200)' }} />
          {!isClosed && (
            <>
              {request.isPaused ? (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ gap: 4, color: 'var(--cds-green-700)' }}
                  onClick={async () => {
                    await resumeClock(request.recordId, actorEmail);
                    const r = await getRequest(request.recordId);
                    if (r) handleRequestUpdate(r);
                  }}
                >
                  Resume Clock
                </button>
              ) : (
                <button className="btn btn-secondary btn-sm" onClick={() => setShowPauseModal(true)}>
                  Pause Clock
                </button>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={async () => {
                  try {
                    const jurisdictions = await listJurisdictions();
                    const jur = jurisdictions.find(j => String(j.recordId) === String(request.jurisdictionId)) ?? jurisdictions[0];
                    if (jur?.maxExtensionDays) setMaxExtensionDays(jur.maxExtensionDays);
                  } catch { /* keep default */ }
                  setShowExtensionModal(true);
                }}
              >
                Grant Extension
              </button>
              {!['denied', 'completed', 'withdrawn'].includes(request.status) && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ color: 'var(--cds-red-700)', borderColor: '#F7D5D0' }}
                  onClick={() => setShowDenialModal(true)}
                >
                  Deny
                </button>
              )}
            </>
          )}
          <button
            className="btn btn-secondary btn-sm"
            style={{ gap: 4 }}
            disabled={aiLoading}
            onClick={async () => {
              setAiLoading(true);
              setActiveTab('overview');
              try {
                const result = await classifyRequest({
                  subject:     request.subject,
                  description: request.description,
                  channel:     request.requestChannel,
                  departments: departments.map(d => d.name),
                });
                setAiResult(result);
              } finally {
                setAiLoading(false);
              }
            }}
          >
            {aiLoading
              ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              : '✦ AI Classify'
            }
          </button>
          <StatusDropdown request={request} actorEmail={actorEmail} onUpdated={handleRequestUpdate} />
          <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} onClick={() => setShowEditModal(true)}>
            <IconEdit size={12} /> Edit
          </button>
        </div>
      </div>

      {/* Metadata bar — single row */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
        padding: '10px 0', marginBottom: 16,
        borderTop: '1px solid var(--color-neutral-200)',
        borderBottom: '1px solid var(--color-neutral-200)',
        fontSize: 15, color: 'var(--color-neutral-600)',
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 10px', borderRadius: 4,
          background: clk.bg, color: clk.text, border: `1px solid ${clk.border}`,
          fontWeight: 600, fontSize: 15, flexShrink: 0,
        }}>
          <IconClock size={15} />
          {formatDaysRemaining(request.daysRemaining ?? 0, request.isPaused ?? false, isClosed)}
          {request.isPaused && <IconAlertTriangle size={13} />}
        </span>
        <span style={{ flexShrink: 0 }}>Due {formatDate(request.effectiveDeadline ?? request.statutoryDeadline)}</span>
        {request.extensionDeadline && (
          <span style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }}>(ext. {formatDate(request.extensionDeadline)})</span>
        )}
        <span style={{ color: 'var(--color-neutral-300)', flexShrink: 0 }}>·</span>
        <span style={{ flexShrink: 0 }}>Submitted {formatDate(request.dateSubmitted)}</span>
        <span style={{ color: 'var(--color-neutral-300)', flexShrink: 0 }}>·</span>
        <span style={{ textTransform: 'capitalize', color: 'var(--color-neutral-500)', flexShrink: 0 }}>{request.requestChannel ?? '—'}</span>
        <span style={{ color: 'var(--color-neutral-300)', flexShrink: 0 }}>·</span>
        <span style={{ color: 'var(--color-neutral-500)', flexShrink: 0 }}>{request.requestType ?? 'FOIA'}</span>
        <span style={{ color: 'var(--color-neutral-300)', flexShrink: 0 }}>|</span>
        <IconUser size={15} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />
        {request.isAnonymous ? (
          <span style={{ fontStyle: 'italic', flexShrink: 0 }}>Anonymous</span>
        ) : requester ? (
          <>
            <span style={{ fontWeight: 600, color: 'var(--color-neutral-800)', flexShrink: 0 }}>{requester.displayName}</span>
            {requester.orgName && <span style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }}>({requester.orgName})</span>}
            {requester.email && (
              <a href={`mailto:${requester.email}`} style={{ color: 'var(--color-primary-700)', textDecoration: 'none', flexShrink: 0 }}>{requester.email}</a>
            )}
            {requester.phone && <span style={{ flexShrink: 0 }}>{formatPhone(requester.phone)}</span>}
          </>
        ) : (
          <span style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }}>{request.requesterId ? 'Loading…' : '—'}</span>
        )}
      </div>

      {/* Phase stepper */}
      <PhaseBar request={request} actorEmail={actorEmail} onUpdated={handleRequestUpdate} />

      {/* Awaiting requester banner */}
      {request.status === 'awaitingRequester' && (
        <AwaitingRequesterBanner
          request={request}
          actorEmail={actorEmail}
          onResolved={handleRequestUpdate}
          onGoToCorrespondence={() => setActiveTab('correspondence')}
        />
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--color-neutral-200)', marginBottom: 24 }}>
        {TABS.map(({ id: tid, label, icon: Icon }) => (
          <button
            key={tid}
            onClick={() => setActiveTab(tid)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px',
              fontSize: 14, fontWeight: activeTab === tid ? 600 : 400,
              color: activeTab === tid ? 'var(--color-primary-700)' : 'var(--color-neutral-600)',
              border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: activeTab === tid ? '2px solid var(--color-primary-700)' : '2px solid transparent',
              marginBottom: -2, transition: 'color 0.15s',
            }}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Tab: Overview */}
      {activeTab === 'overview' && (
        <div>
          {(aiResult || request.aiDeptSuggestion || request.aiComplexity) && (() => {
            const storedFlags: string[] = (() => {
              try { return request.aiFlags ? JSON.parse(request.aiFlags) : []; }
              catch { return []; }
            })();
            const displayFlags = aiResult?.flags?.length ? aiResult.flags : storedFlags;
            return (
              <div style={{
                marginBottom: 16, padding: '12px 16px',
                background: 'linear-gradient(135deg, var(--cds-blurple-50), #F5F3FF)',
                border: '1px solid #D4DDFB', borderRadius: 4,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-700)', marginBottom: 8 }}>✦ AI Analysis</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', fontSize: 14 }}>
                  {(aiResult?.suggestedDepartment || request.aiDeptSuggestion) && (
                    <div><span style={{ color: 'var(--color-primary-700)', fontWeight: 500 }}>Dept:</span> {aiResult?.suggestedDepartment || request.aiDeptSuggestion}</div>
                  )}
                  {(aiResult?.complexity || request.aiComplexity) && (
                    <div><span style={{ color: 'var(--color-primary-700)', fontWeight: 500 }}>Complexity:</span> {aiResult?.complexity || request.aiComplexity}</div>
                  )}
                  {aiResult?.estimatedDays && (
                    <div><span style={{ color: 'var(--color-primary-700)', fontWeight: 500 }}>Est. days:</span> {aiResult.estimatedDays}</div>
                  )}
                </div>
                {displayFlags.length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {displayFlags.map(f => (
                      <span key={f} style={{ fontSize: 12, fontWeight: 600, padding: '2px 7px', borderRadius: 99, background: 'var(--cds-red-50)', color: 'var(--cds-red-700)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f}</span>
                    ))}
                  </div>
                )}
                {aiResult?.summary && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-primary-700)', lineHeight: 1.5 }}>{aiResult.summary}</div>
                )}
              </div>
            );
          })()}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="card">
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-neutral-100)' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Request Description</span>
              </div>
              <div style={{ padding: '14px 16px', fontSize: 14, color: 'var(--color-neutral-700)', lineHeight: 1.7 }}>
                {request.description || <span style={{ color: 'var(--color-neutral-400)', fontStyle: 'italic' }}>No description provided.</span>}
              </div>
            </div>

            <div className="card">
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-neutral-100)' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Details</span>
              </div>
              <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                {([
                  ['Priority',     request.priority ?? 'Normal'],
                  ['Request Type', request.requestType ?? 'FOIA'],
                  ['Assignee',     request.assignedUserId || 'Unassigned'],
                  ['Extension',    request.extensionDeadline ? `Extended to ${formatDate(request.extensionDeadline)}` : 'None'],
                ] as [string, string][]).map(([label, value]) => (
                  <div key={label}>
                    <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 14, color: 'var(--color-neutral-800)', fontWeight: 500, textTransform: label === 'Priority' ? 'capitalize' : undefined }}>{value}</div>
                  </div>
                ))}
                {requester && (
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Requester Account</div>
                    {requester.isRegistered ? (
                      <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: 'var(--cds-green-50)', color: 'var(--cds-green-700)', border: '1px solid var(--cds-green-200)' }}>
                        ● Registered
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: 'var(--color-neutral-100)', color: 'var(--color-neutral-500)', border: '1px solid var(--color-neutral-200)' }}>
                        ○ Guest
                      </span>
                    )}
                  </div>
                )}
              </div>
              {request.internalNotes && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid var(--color-neutral-100)' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Internal Notes</div>
                  <div style={{ fontSize: 14, color: 'var(--color-neutral-700)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{request.internalNotes}</div>
                </div>
              )}
            </div>
          </div>

          {/* Internal Notes */}
          {internalNotes.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Internal Notes
                </span>
                <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
                  {internalNotes.length} note{internalNotes.length !== 1 ? 's' : ''} · staff only
                </span>
              </div>
              <div>
                {internalNotes.map((note, i) => (
                  <div key={note.recordId} style={{
                    padding: '12px 16px',
                    borderBottom: i < internalNotes.length - 1 ? '1px solid var(--color-neutral-100)' : undefined,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-700)' }}>
                        {note.fromUserId || 'Staff'}
                      </span>
                      {note.subject && (
                        <span style={{ fontSize: 12, color: 'var(--color-neutral-500)', fontStyle: 'italic' }}>
                          {note.subject}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginLeft: 'auto' }}>
                        {note.sentAt ? new Date(note.sentAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--color-neutral-700)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {note.body}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Documents */}
      {activeTab === 'documents' && (
        <div className="card" style={{ padding: 16 }}>
          <DocumentsTab
            requestId={request.recordId}
            actorEmail={actorEmail}
            requesterEmail={requester?.email}
            requesterName={requester?.displayName}
            requestNumber={request.requestNumber}
          />
        </div>
      )}

      {/* Tab: Tasks */}
      {activeTab === 'tasks' && (
        <div className="card" style={{ padding: 16 }}>
          <TasksTab requestId={request.recordId} actorEmail={actorEmail} deadlineDate={request.effectiveDeadline} />
        </div>
      )}

      {/* Tab: Correspondence */}
      {activeTab === 'correspondence' && (
        <div className="card" style={{ padding: 16 }}>
          <CorrespondenceTab
            requestId={request.recordId}
            request={request}
            requester={requester}
            actorEmail={actorEmail}
            jurisdictionId={request.jurisdictionId ?? '1'}
            onStatusChange={async () => {
              const updated = await getRequest(request.recordId);
              if (updated) handleRequestUpdate(updated);
            }}
          />
        </div>
      )}

      {/* Tab: Invoices */}
      {activeTab === 'invoices' && (
        <div className="card" style={{ padding: 16 }}>
          <InvoicesTab
            requestId={request.recordId}
            requesterId={request.requesterId}
            actorEmail={actorEmail}
            request={request}
            requester={requester}
          />
        </div>
      )}

      {/* Tab: Timeline */}
      {activeTab === 'timeline' && (
        <div className="card" style={{ padding: 16 }}>
          <TimelineTab requestId={request.recordId} />
        </div>
      )}
    </div>
  );
}
